/**
 * "SELECT ALL" FOR THE MULTI-SELECT — the decision, kept pure.
 *
 * Every picker built on `MultiSelect` (branches, countries, roles, users,
 * sources, stages… 27 of them) filters its rows item-by-item against the
 * search box, so the list header cannot see what is on offer. The items
 * therefore REGISTER themselves (value, searchable label, disabled, bulk)
 * and this helper decides, from that registry, what "Select all" means
 * right now:
 *   - it acts on the VISIBLE rows — everything when the search is empty,
 *     the matches when it is not (owner, 2026-09-24: "I need select all
 *     options for all these multi selector options");
 *   - a sentinel like "No branch" / "No Country" (`value === "none"`, the
 *     leads page's CF_NONE_SENTINEL) is never bulk-selected: "no value" and
 *     "these values" contradict each other on the list;
 *   - once every visible row is selected the same control CLEARS them, and
 *     rows hidden by the search are left exactly as they were.
 */

export interface BulkItem {
  value: string;
  /** What the search box matches against — the item's own rule. */
  label: string;
  disabled: boolean;
  /** False for a sentinel row that must never be swept up. */
  bulk: boolean;
}

/** The ONE search rule, shared with `MultiSelectItem` so the row a user can
 *  see and the row "Select all" counts can never disagree. */
export function matchesSearch(label: string, search: string): boolean {
  const q = search.trim().toLowerCase();
  return !q || label.toLowerCase().includes(q);
}

export interface BulkSelection {
  /** Values "Select all" would act on, in registration order. */
  visible: string[];
  /** Every visible value is already selected → the control clears. */
  allSelected: boolean;
  /** The value list after the control is pressed. */
  next: string[];
}

export function bulkSelection(items: Iterable<BulkItem>, value: string[], search: string): BulkSelection {
  const visible: string[] = [];
  for (const item of items) {
    if (item.bulk && !item.disabled && matchesSearch(item.label, search)) visible.push(item.value);
  }
  const chosen = new Set(value);
  const allSelected = visible.length > 0 && visible.every((v) => chosen.has(v));
  const next = allSelected
    ? value.filter((v) => !visible.includes(v))
    : [...value, ...visible.filter((v) => !chosen.has(v))];
  return { visible, allSelected, next };
}
